

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

export function getTourStorageKey(userId) {
  if (userId) return `financial_dashboard_tour_started_${userId}`;
  return 'financial_dashboard_tour_started';
}


function resolveSelector(desktopSelector, mobileSelector) {
  const isMobile = window.innerWidth <= 768;
  const preferred = isMobile ? mobileSelector : desktopSelector;
  if (document.querySelector(preferred)) return preferred;
  if (document.querySelector(isMobile ? desktopSelector : mobileSelector)) {
    return isMobile ? desktopSelector : mobileSelector;
  }
  return preferred;
}


function stepContent(title, body, current, total) {
  const pills = Array.from({ length: total }, (_, i) => {
    const isActive = i < current;
    return `<span class="fd-pill${isActive ? ' fd-pill--active' : ''}"></span>`;
  }).join('');

  return [
    `<span class="fd-tour-progress-bar">${pills}</span>`,
    `<strong class="fd-tour-step-title">${title}</strong>`,
    `<span class="fd-tour-step-body">${body}</span>`,
  ].join('');
}

export function buildTourSteps() {
  const steps = [
    // Step 1: Financial / Business Dashboard toggle
    {
      element: resolveSelector(
        '[data-tour="dashboard-toggle"]',
        '[data-tour="mobile-dashboard-toggle"]'
      ),
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Financial / Business Dashboard',
        _stepBody: 'Switch between financial insights and business performance views.',
      },
    },

    // Step 2: Filter Chips overview
    {
      element: resolveSelector(
        '[data-tour="filter-bar"]',
        '[data-tour="mobile-filter-toggle"]'
      ),
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Filter Chips',
        _stepBody: 'Refine your data using multiple filters like distributor, location, and category.',
      },
    },

    {
      element: resolveSelector(
        '[data-tour="filter-date-range"]',
        '[data-tour="mobile-filter-date-range"]'
      ),
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Date Range Selector',
        _stepBody: 'Analyze performance over custom or preset time periods.',
      },
    },

    {
      element: resolveSelector(
        '[data-tour="filter-bar"]',
        '[data-tour="mobile-filter-toggle"]'
      ),
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Active Filters Bar',
        _stepBody: "View and manage currently applied filters. Click 'Clear All' to reset.",
      },
    },

    {
      element: resolveSelector(
        '[data-tour="filter-apply"]',
        '[data-tour="mobile-filter-apply"]'
      ),
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Apply Filter Button',
        _stepBody: 'Apply selected filters to update all dashboard metrics instantly.',
      },
    },

    {
      element: resolveSelector(
        '[data-tour="company-selector"]',
        '[data-tour="mobile-company-selector"]'
      ),
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Company Selector',
        _stepBody: 'Change the company to view its specific data and reports.',
      },
    },

    {
      element: '[data-tour="dashboard-tabs"]',
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Tabs',
        _stepBody: 'Switch between different data perspectives to focus on what matters most.',
      },
    },

    {
      element: '[data-tour="receivables-section"]',
      popover: {
        title: 'Tutorials',
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Receivables & Payables',
        _stepBody: 'Track outstanding dues and payables at a glance. Expand for a full aging breakdown.',
      },
    },
  ];

  const total = steps.length;
  steps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(
      step.popover._stepTitle,
      step.popover._stepBody,
      i + 1,   // current (1-based)
      total
    );
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });

  return steps;
}



export function buildCatalogTourSteps() {
  const isMobile = window.innerWidth <= 768;
  const rawSteps = [
    {
      element: isMobile ? '[data-tour="mobile-catalog-link"]' : '[data-tour="catalog-sidebar-link"]',
      popover: { side: 'right', align: 'start', _stepTitle: 'Catalog', _stepBody: 'Manage your product listings and updates.' },
    },
    {
      element: '[data-tour="catalog-upload-btn"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Upload Product', _stepBody: 'Create detailed product listings for better visibility.' },
    },
    {
      element: '[data-tour="catalog-product-image"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Upload Product Image', _stepBody: 'Add an image to make your listing stand out.' },
    },
    {
      element: '[data-tour="catalog-bestselling-btn"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Set as Best Selling', _stepBody: 'Feature this product as a popular choice.' },
    },
    {
      element: '[data-tour="catalog-remove-bestselling-btn"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Remove as Best Selling', _stepBody: 'This product will no longer appear as best selling.' },
    },
    {
      element: '[data-tour="catalog-filter-sort"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Filter and Sort By', _stepBody: 'Refine results and change the display order.' },
    },
    {
      element: '[data-tour="catalog-edit-btn"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Edit', _stepBody: 'Update product details and information.' },
    },
    {
      element: '[data-tour="catalog-status-btn"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Status', _stepBody: "Indicates the item's current condition." },
    },
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function getCatalogTourStorageKey(userId) {
  return userId ? `catalog_tour_started_${userId}` : 'catalog_tour_started';
}

export function getExploreTourStorageKey(userId) {
  return userId ? `explore_tour_started_${userId}` : 'explore_tour_started';
}

export function useCatalogTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getCatalogTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch (error) { console.warn('[tour] localStorage read failed:', error); return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getCatalogTourStorageKey(userId)));

  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch (error) { console.error('[useCatalogTour] Failed to save tour flag:', error); }
    setTourStarted(true);
  }, []);

  const startTour = useCallback(() => {
    if (driverRef.current) { try { driverRef.current.destroy(); } catch (error) { console.error('[useCatalogTour] Failed to destroy driver:', error); } driverRef.current = null; }

    const visibleSteps = buildCatalogTourSteps();
    if (visibleSteps.length === 0) { console.warn('[useCatalogTour] No visible tour elements found.'); return; }

    const driverInstance = driver({
      animate: true, smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)', overlayOpacity: 0.55,
      stagePadding: 8, stageRadius: 10,
      popoverClass: 'fd-tour-popover', showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next', doneBtnText: 'Done', closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },
      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;
        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());
          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);
          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }
        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();
          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },
      onNextClick: () => driverInstance.moveNext(),
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => { driverRef.current = null; },
      steps: visibleSteps,
    });

    driverRef.current = driverInstance;
    driverInstance.drive();
    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => { if (driverRef.current) { try { driverRef.current.destroy(); } catch (error) { console.error('[useCatalogTour] Failed to destroy driver on unmount:', error); } driverRef.current = null; } };
  }, []);

  return { startTour, tourStarted };
}

export function useDashboardTour(userId) {
  const driverRef = useRef(null);

  const storageKey = getTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch (error) { console.warn('[tour] localStorage read failed:', error); return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getTourStorageKey(userId)));

  useEffect(() => {
    setTourStarted(readFlag(storageKey));
  }, [storageKey, readFlag]);


  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch (error) { console.error('[useDashboardTour] Failed to save tour flag:', error); }
    setTourStarted(true);
  }, []);


  const startTour = useCallback(() => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch (error) { console.error('[useDashboardTour] Failed to destroy driver:', error); }
      driverRef.current = null;
    }

    const allSteps = buildTourSteps();
    const visibleSteps = allSteps.filter(step => {
      if (!step.element) return true;

      if (step.element === '[data-tour="mobile-filter-date-range"]') return true;

      // Active filters bar only renders when tags are present — skip gracefully if absent
      // Aging / customer breakdown steps only exist on the Receivables page — skip if not in DOM
      return !!document.querySelector(step.element);
    });

    if (visibleSteps.length === 0) {
      console.warn('[useDashboardTour] No visible tour elements found.');
      return;
    }

    const isMobile = window.innerWidth <= 768;

    const key = getTourStorageKey(userId);

    const driverInstance = driver({
      animate: true,
      smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 10,

      popoverClass: 'fd-tour-popover',
      showProgress: false,

      allowHTMLInPopover: true,

      nextBtnText: 'Next',
      doneBtnText: 'Done',
      closeBtnText: 'Skip',

      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },


      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;


        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;

          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());

          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);

          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }

        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();

          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => {
            driverInstance.destroy();
          });
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },

      onNextClick: () => {
        const currentIndex = driverInstance.getActiveIndex?.() ?? -1;
        const nextStep = visibleSteps[currentIndex + 1];
        const currentStep = visibleSteps[currentIndex];

        if (isMobile) {
          const openFilterPanel = () => {
            const panel = document.querySelector('.mobile-filter-panel');
            const isOpen = panel?.classList.contains('mobile-filter-panel--open');
            if (!isOpen) {
              const toggleBtn = document.querySelector('[data-tour="mobile-filter-toggle"] .mobile-filter-btn');
              if (toggleBtn) toggleBtn.click();
            }
          };

          const closeFilterPanel = () => {
            const panel = document.querySelector('.mobile-filter-panel');
            const isOpen = panel?.classList.contains('mobile-filter-panel--open');
            if (isOpen) {
              const closeBtn = document.querySelector('.mobile-filter-close');
              if (closeBtn) closeBtn.click();
            }
          };

          if (
            nextStep?.element === '[data-tour="mobile-filter-date-range"]' ||
            nextStep?.element === '[data-tour="mobile-filter-apply"]'
          ) {
            openFilterPanel();
            setTimeout(() => { driverInstance.moveNext(); }, 350);
            return;
          }

          if (
            currentStep?.element === '[data-tour="mobile-filter-date-range"]' ||
            currentStep?.element === '[data-tour="mobile-filter-apply"]'
          ) {
            closeFilterPanel();
            setTimeout(() => { driverInstance.moveNext(); }, 350);
            return;
          }
        }

        driverInstance.moveNext();
      },

      onDestroyStarted: () => {
        driverInstance.destroy();
      },
      onDestroyed: () => {
        driverRef.current = null;
        if (isMobile) {
          const closeBtn = document.querySelector('.mobile-filter-close');
          if (closeBtn) closeBtn.click();
        }
      },

      steps: visibleSteps,
    });

    driverRef.current = driverInstance;
    driverInstance.drive();

    markStarted(key);
  }, [userId, markStarted]);


  useEffect(() => {
    return () => {
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch (error) {
          console.error('Failed to destroy driver instance:', error);

        }
        driverRef.current = null;
      }
    };
  }, []);

  return { startTour, tourStarted };
}

export function buildExploreTourSteps() {
  const rawSteps = [
    {
      element: '[data-tour="explore-view-profile"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Explore Partner', _stepBody: 'Explore partner profiles to review products, company details, and location.' },
    },
    {
      element: '[data-tour="explore-save-profile"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Save Profile', _stepBody: "Save profiles you're interested in and connect when ready." },
    },
    {
      element: '[data-tour="explore-connect"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Connect', _stepBody: 'Send a connection request to start a business conversation with this partner.' },
    },
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function useExploreTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getExploreTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch (error) { console.warn('[tour] localStorage read failed:', error); return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getExploreTourStorageKey(userId)));

  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch (error) { console.error('[useExploreTour] Failed to save tour flag:', error); }
    setTourStarted(true);
  }, []);

  const startTour = useCallback((onComplete) => {
    if (driverRef.current) { try { driverRef.current.destroy(); } catch (error) { console.error('[useExploreTour] Failed to destroy driver:', error); } driverRef.current = null; }

    const visibleSteps = buildExploreTourSteps();
    if (visibleSteps.length === 0) { console.warn('[useExploreTour] No visible tour elements found.'); return; }

    const driverInstance = driver({
      animate: true, smoothScroll: false,
      overlayColor: 'rgba(0, 0, 0, 0.55)', overlayOpacity: 0.55,
      stagePadding: 8, stageRadius: 10,
      popoverClass: 'fd-tour-popover', showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next', doneBtnText: 'Done', closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'instant', block: 'center' },
      onHighlightStarted: (element) => {
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      },
      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;
        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());
          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);
          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }
        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();
          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },
      onNextClick: () => {
        const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
        const nextStep = visibleSteps[currentIndex + 1];
        const nextEl = nextStep?.element ? document.querySelector(nextStep.element) : null;
        if (nextEl) {
          nextEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => { driverInstance.moveNext(); }, 400);
        } else {
          driverInstance.moveNext();
        }
      },
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => {
        driverRef.current = null;
        window.dispatchEvent(new CustomEvent('exploreTourEnd'));
        if (typeof onComplete === 'function') onComplete();
      },
      steps: visibleSteps,
    });

    driverRef.current = driverInstance;
    window.dispatchEvent(new CustomEvent('exploreTourStart'));

    // Scroll first element into view and wait for scroll to settle before driving
    const firstEl = visibleSteps[0]?.element ? document.querySelector(visibleSteps[0].element) : null;
    if (firstEl) {
      firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { driverInstance.drive(); }, 500);
    } else {
      driverInstance.drive();
    }

    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => { if (driverRef.current) { try { driverRef.current.destroy(); } catch (error) { console.error('[useExploreTour] Failed to destroy driver on unmount:', error); } driverRef.current = null; } };
  }, []);

  return { startTour, tourStarted };
}




export function getSalesMappingTourStorageKey(userId) {
  return userId ? `sales_mapping_tour_started_${userId}` : 'sales_mapping_tour_started';
}

export function buildSalesMappingTourSteps() {
  const isMobile = window.innerWidth <= 768;

  const rawSteps = [
    {
      element: resolveSelector(
        '[data-tour="sales-mapping-tab"]',
        '[data-tour="sales-mapping-tab-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Sales Team Mapping',
        _stepBody: 'Choose a sales team member to assign responsibility.',
      },
    },
    {
      element: resolveSelector(
        '[data-tour="unmapped-distributor-tab"]',
        '[data-tour="unmapped-distributor-tab-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Unmapped Distributor',
        _stepBody: 'View distributors that are not yet assigned to any sales team.',
      },
    },
    {
      element: resolveSelector(
        '[data-tour="sales-mapping-sidebar-link"]',
        '[data-tour="sales-mapping-sidebar-link-mobile"]'
      ),
      popover: {
        side: isMobile ? 'bottom' : 'right',
        align: 'start',
        _stepTitle: 'Distributor Staff Mapping',
        _stepBody: 'Assign distributor staff to manage specific distributors, regions, or accounts.',
      },
    },
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function useSalesMappingTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getSalesMappingTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch (error) { console.warn('[tour] localStorage read failed:', error); return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getSalesMappingTourStorageKey(userId)));

  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch (error) { console.error('[useSalesMappingTour] Failed to save tour flag:', error); }
    setTourStarted(true);
  }, []);

  const startTour = useCallback(() => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch (error) { console.error('[useSalesMappingTour] Failed to destroy driver:', error); }
      driverRef.current = null;
    }

    const visibleSteps = buildSalesMappingTourSteps();
    if (visibleSteps.length === 0) {
      console.warn('[useSalesMappingTour] No visible tour elements found.');
      return;
    }

    const driverInstance = driver({
      animate: true,
      smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 10,
      popoverClass: 'fd-tour-popover',
      showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next',
      doneBtnText: 'Done',
      closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },

      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;

        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());

          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);

          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }

        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();

          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },

      onNextClick: () => driverInstance.moveNext(),
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => { driverRef.current = null; },

      steps: visibleSteps,
    });

    driverRef.current = driverInstance;
    driverInstance.drive();
    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch (error) { console.error('[useSalesMappingTour] Failed to destroy driver on unmount:', error); }
        driverRef.current = null;
      }
    };
  }, []);

  return { startTour, tourStarted };
}


export function getSales2TourStorageKey(userId) {
  return userId ? `sales2_tour_started_${userId}` : 'sales2_tour_started';
}


export function buildSales2TourSteps({ hasComparison = false, hasSalesSplit = false } = {}) {
  const isMobile = window.innerWidth <= 768;

  const rawSteps = [
    {
      element: '[data-tour="sales2-primary-tab"]',
      popover: {
        side: 'right',
        align: 'start',
        _stepTitle: 'Primary Sales',
        _stepBody: 'Track your primary sales performance across distributors.',
      },
    },

    ...(!hasComparison ? [{
      element: isMobile
        ? '[data-tour="sales2-mobile-sparkline"]'
        : '[data-tour="sales2-sparkline"]',
      popover: {
        side: 'top',
        align: 'start',
        _stepTitle: 'Sparkline Graph',
        _stepBody: 'Quick trend view<br>• Green trend → Growth<br>• Red trend → Decline',
      },
    }] : []),

    ...(!hasSalesSplit ? [{
      element: isMobile
        ? '[data-tour="sales2-mobile-sales-col"]'
        : '[data-tour="sales2-sales-col"]',
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Sales Column',
        _stepBody: '• Current sales value<br>• \u201cVs Last Month Value\u201d<br>&nbsp;&nbsp;&nbsp;&nbsp;\u2192 Direct comparison with previous period',
      },
    }] : []),

    ...(hasComparison ? [{
      element: isMobile
        ? '[data-tour="sales2-mobile-sparkline-comparison"]'
        : '[data-tour="sales2-sparkline-comparison"]',
      popover: {
        side: 'top',
        align: 'start',
        _stepTitle: 'Sparkline (Trend + Comparison)',
        _stepBody: '• Solid line \u2192 Current period<br>• Dotted line \u2192 Previous period',
      },
    }] : []),

    {
      element: isMobile
        ? '[data-tour="sales2-mobile-share-col"]'
        : '[data-tour="sales2-share-col"]',
      popover: {
        side: 'top',
        align: 'start',
        _stepTitle: 'Share Column',
        _stepBody: '• Circular % contribution<br>• \u201cPrev XX%\u201d indicator<br>&nbsp;&nbsp;&nbsp;&nbsp;\u2192 Shows how share has changed',
      },
    },

    ...(hasSalesSplit ? [{
      element: isMobile
        ? '[data-tour="sales2-mobile-split-sales-col"]'
        : '[data-tour="sales2-split-sales-col"]',
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Secondary Sales Enabled',
        _stepBody: '• PR (Primary Sales) \u2192 Sales to distributor<br>• SC (Secondary Sales) \u2192 Sales from distributor to retailer',
      },
    }] : []),
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;

  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });

  return visibleSteps;
}

export function useSales2Tour(userId) {
  const driverRef = useRef(null);
  const storageKey = getSales2TourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch (error) { console.warn('[tour] localStorage read failed:', error); return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getSales2TourStorageKey(userId)));

  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch (error) { console.error('[useSales2Tour] Failed to save tour flag:', error); }
    setTourStarted(true);
  }, []);


  const startTour = useCallback((options = {}) => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch (error) { console.error('[useSales2Tour] Failed to destroy driver:', error); }
      driverRef.current = null;
    }

    const visibleSteps = buildSales2TourSteps(options);
    if (visibleSteps.length === 0) {
      console.warn('[useSales2Tour] No visible tour elements found.');
      return;
    }

    const driverInstance = driver({
      animate: true,
      smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 10,
      popoverClass: 'fd-tour-popover',
      showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next',
      doneBtnText: 'Done',
      closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },

      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;

        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());

          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);

          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }

        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();

          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },

      onNextClick: () => driverInstance.moveNext(),
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => { driverRef.current = null; },

      steps: visibleSteps,
    });

    driverRef.current = driverInstance;
    driverInstance.drive();
    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch (error) { console.error('[useSales2Tour] Failed to destroy driver on unmount:', error); }
        driverRef.current = null;
      }
    };
  }, []);

  return { startTour, tourStarted };
}


export function getIndustryCategoryTourStorageKey(userId) {
  return userId ? `industry_category_tour_started_${userId}` : 'industry_category_tour_started';
}

export function buildIndustryCategoryTourSteps() {
  const isMobile = window.innerWidth <= 768;

  const rawSteps = [
    {
      element: resolveSelector(
        '[data-tour="ic-category-tab"]',
        '[data-tour="ic-category-tab-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Category',
        _stepBody: 'View and manage all product categories in one place.',
      },
    },
    {
      element: resolveSelector(
        '[data-tour="ic-subcategory-tab"]',
        '[data-tour="ic-subcategory-tab-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Subcategory',
        _stepBody: 'Organize products further by grouping them into detailed subcategories.',
      },
    },
    {
      element: isMobile
        ? '[data-tour="ic-create-btn-mobile"]'
        : '[data-tour="ic-add-category-btn"]',
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Add Category',
        _stepBody: 'Enter a clear and unique name for your category.',
      },
    },
    {
      element: isMobile
        ? '[data-tour="ic-create-btn-mobile"]'
        : '[data-tour="ic-add-subcategory-btn"]',
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Add Sub Category',
        _stepBody: 'Create a subcategory to further organize products within a category.',
      },
    },
    {
      element: resolveSelector(
        '[data-tour="ic-filter-btn"]',
        '[data-tour="ic-filter-btn-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'end',
        _stepTitle: 'Filter By',
        _stepBody: 'Choose how you want to aggregate and analyze your data.',
      },
    },
    {
      element: resolveSelector(
        '[data-tour="ic-sku-mapping-tab"]',
        '[data-tour="ic-sku-mapping-tab-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'SKU Mapping',
        _stepBody: 'View and manage how SKUs are mapped across categories.',
      },
    },
    {
      element: resolveSelector(
        '[data-tour="ic-unmapped-sku-tab"]',
        '[data-tour="ic-unmapped-sku-tab-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Unmapped SKUs',
        _stepBody: 'View all SKUs that are not yet assigned to any category or subcategory.',
      },
    },
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}


export function getSku2TourStorageKey(userId) {
  return userId ? `sku2_tour_started_${userId}` : 'sku2_tour_started';
}

export function buildSku2TourSteps({ hasBaseUnitToggle = false } = {}) {
  const isMobile = window.innerWidth <= 768;

  const rawSteps = [
    {
      element: resolveSelector(
        '[data-tour="sku2-sidebar-link"]',
        '[data-tour="sku2-sidebar-link-mobile"]'
      ),
      popover: {
        side: isMobile ? 'bottom' : 'right',
        align: 'start',
        _stepTitle: 'My SKUs',
        _stepBody: 'Track performance, inventory, and trends of all your SKUs in one place.',
      },
    },

    {
      element: resolveSelector(
        '[data-tour="sku2-filter-bar"]',
        '[data-tour="sku2-filter-bar-mobile"]'
      ),
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Refine Your Data',
        _stepBody: 'Filter SKUs by location, category, and time range to view relevant insights.',
      },
    },

    ...(hasBaseUnitToggle ? [{
      element: '[data-tour="sku2-base-unit-btn"]',
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Base Unit',
        _stepBody: 'View quantity in individual pieces — the smallest sellable unit.',
      },
    }] : []),

    ...(hasBaseUnitToggle ? [{
      element: '[data-tour="sku2-pack-unit-btn"]',
      popover: {
        side: 'bottom',
        align: 'start',
        _stepTitle: 'Pack Unit',
        _stepBody: 'View quantity in case or pack configuration as per your case setup.',
      },
    }] : []),
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function useSku2Tour(userId) {
  const driverRef = useRef(null);
  const storageKey = getSku2TourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch (error) { console.warn('[tour] localStorage read failed:', error); return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getSku2TourStorageKey(userId)));

  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch (error) { console.error('[useSku2Tour] Failed to save tour flag:', error); }
    setTourStarted(true);
  }, []);

  const startTour = useCallback((options = {}) => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch (error) { console.error('[useSku2Tour] Failed to destroy driver:', error); }
      driverRef.current = null;
    }

    const visibleSteps = buildSku2TourSteps(options);
    if (visibleSteps.length === 0) {
      console.warn('[useSku2Tour] No visible tour elements found.');
      return;
    }

    const driverInstance = driver({
      animate: true,
      smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 10,
      popoverClass: 'fd-tour-popover fd-tour-popover--with-arrow',
      showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next',
      doneBtnText: 'Done',
      closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },

      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;

        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());

          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);

          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }

        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();

          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },

      onNextClick: () => driverInstance.moveNext(),
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => { driverRef.current = null; },

      steps: visibleSteps,
    });

    driverRef.current = driverInstance;
    driverInstance.drive();
    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch (error) { console.error('[useSku2Tour] Failed to destroy driver on unmount:', error); }
        driverRef.current = null;
      }
    };
  }, []);

  return { startTour, tourStarted };
}

export function useIndustryCategoryTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getIndustryCategoryTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch (error) { console.warn('[tour] localStorage read failed:', error); return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getIndustryCategoryTourStorageKey(userId)));

  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch (error) { console.error('[useIndustryCategoryTour] Failed to save tour flag:', error); }
    setTourStarted(true);
  }, []);

  const startTour = useCallback(() => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch (error) { console.error('[useIndustryCategoryTour] Failed to destroy driver:', error); }
      driverRef.current = null;
    }

    const visibleSteps = buildIndustryCategoryTourSteps();
    if (visibleSteps.length === 0) {
      console.warn('[useIndustryCategoryTour] No visible tour elements found.');
      return;
    }

    const driverInstance = driver({
      animate: true,
      smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 10,
      popoverClass: 'fd-tour-popover',
      showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next',
      doneBtnText: 'Done',
      closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },

      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;

        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());

          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);

          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }

        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();

          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },

      onNextClick: () => driverInstance.moveNext(),
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => { driverRef.current = null; },

      steps: visibleSteps,
    });

    driverRef.current = driverInstance;
    driverInstance.drive();
    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch (error) { console.error('[useIndustryCategoryTour] Failed to destroy driver on unmount:', error); }
        driverRef.current = null;
      }
    };
  }, []);

  return { startTour, tourStarted };
}

export function getDashboardBusinessTourStorageKey(userId) {
  return userId ? `dashboard_business_tour_started_${userId}` : 'dashboard_business_tour_started';
}

export function buildDashboardBusinessTourSteps() {
  const ctaEl = document.querySelector('[data-tour="dashboard-setup-cta"]');
  const ctaText = ctaEl?.textContent?.trim() || '';

  let ctaTitle = 'Get Started';
  let ctaBody = 'Complete this action to move forward.';

  if (ctaText.toLowerCase().includes('setup') || ctaText.toLowerCase().includes('set up')) {
    ctaTitle = 'Setup Now';
    ctaBody = 'Finish setting up your profile to start connecting with businesses.';
  } else if (ctaText.toLowerCase().includes('verified')) {
    ctaTitle = 'Get Verified';
    ctaBody = 'Verify your business to build trust and improve visibility on the platform.';
  } else if (ctaText.toLowerCase().includes('product')) {
    ctaTitle = 'Add your Product';
    ctaBody = 'Create your product catalog and grow your business reach.';
  } else if (ctaText.toLowerCase().includes('requirement')) {
    ctaTitle = 'Add Your Requirement';
    ctaBody = 'Post your requirements to connect with the right manufacturers.';
  }

  const rawSteps = [
    ...(ctaEl ? [{
      element: '[data-tour="dashboard-setup-cta"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: ctaTitle, _stepBody: ctaBody },
    }] : []),
    {
      element: '[data-tour="dashboard-view-profile"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'View Profile', _stepBody: "View this business's profile and latest information." },
    },
    {
      element: '[data-tour="dashboard-connect"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Connect', _stepBody: 'Request a connection to start engaging with this business.' },
    },
    {
      element: '[data-tour="dashboard-stat-connections"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Total Connections', _stepBody: "Total Connections here consists of all Businesses you're connected with." },
    },
    {
      element: '[data-tour="dashboard-stat-deals"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Total Deals', _stepBody: 'Includes active, pending, and completed deals.' },
    },
    {
      element: '[data-tour="dashboard-stat-conversion"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Conversion Rate', _stepBody: 'Percentage of connections that turned into deals.' },
    },
    {
      element: '[data-tour="dashboard-stat-pending"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Pending Requests', _stepBody: "Requests that haven't been accepted or declined yet." },
    },
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function useDashboardBusinessTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getDashboardBusinessTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch { return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getDashboardBusinessTourStorageKey(userId)));

  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch { /* ignore */ }
    setTourStarted(true);
  }, []);


  const startTour = useCallback((onComplete) => {
    if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; }

    // Dispatch start event first so isTourActive=true causes cards to render data-tour attrs
    window.dispatchEvent(new CustomEvent('dashboardBusinessTourStart'));

    const launchTour = () => {
      const visibleSteps = buildDashboardBusinessTourSteps();
      if (visibleSteps.length === 0) { console.warn('[useDashboardBusinessTour] No visible tour elements found.'); return; }

      const driverInstance = driver({
        animate: true, smoothScroll: false,
        overlayColor: 'rgba(0, 0, 0, 0.55)', overlayOpacity: 0.55,
        stagePadding: 8, stageRadius: 10,
        popoverClass: 'fd-tour-popover', showProgress: false,
        allowHTMLInPopover: true,
        nextBtnText: 'Next', doneBtnText: 'Done', closeBtnText: 'Skip',
        scrollIntoViewOptions: { behavior: 'instant', block: 'center' },
        onHighlightStarted: (element) => {
          if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        onPopoverRender: (popover) => {
          const titleEl = popover.title;
          const footerEl = popover.footer;
          if (titleEl) {
            const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
            const total = visibleSteps.length;
            titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());
            const labelSpan = document.createElement('span');
            labelSpan.className = 'fd-title-text';
            labelSpan.textContent = 'Tutorials';
            titleEl.insertBefore(labelSpan, titleEl.firstChild);
            const counter = document.createElement('span');
            counter.className = 'fd-progress-counter';
            counter.textContent = `${currentIndex + 1} of ${total}`;
            titleEl.appendChild(counter);
          }
          if (footerEl) {
            const oldSkip = footerEl.querySelector('.fd-skip-btn');
            if (oldSkip) oldSkip.remove();
            const skipBtn = document.createElement('button');
            skipBtn.className = 'fd-skip-btn';
            skipBtn.textContent = 'Skip';
            skipBtn.type = 'button';
            skipBtn.addEventListener('click', () => driverInstance.destroy());
            footerEl.insertBefore(skipBtn, footerEl.firstChild);
          }
        },
        onNextClick: () => {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const nextStep = visibleSteps[currentIndex + 1];
          const nextEl = nextStep?.element ? document.querySelector(nextStep.element) : null;
          if (nextEl) {
            nextEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => { driverInstance.moveNext(); }, 400);
          } else {
            driverInstance.moveNext();
          }
        },
        onDestroyStarted: () => driverInstance.destroy(),
        onDestroyed: () => {
          driverRef.current = null;
          window.dispatchEvent(new CustomEvent('dashboardBusinessTourEnd'));
          if (typeof onComplete === 'function') onComplete();
        },
        steps: visibleSteps,
      });

      driverRef.current = driverInstance;

      const firstEl = visibleSteps[0]?.element ? document.querySelector(visibleSteps[0].element) : null;
      if (firstEl) {
        firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => { driverInstance.drive(); }, 500);
      } else {
        driverInstance.drive();
      }

      markStarted(storageKey);
    }; // end launchTour

    // Poll for card elements to appear (cards need isTourActive=true to render data-tour attrs)
    const cardSelector = '[data-tour="dashboard-view-profile"]';
    const maxWait = 10000;
    const pollInterval = 100;
    let elapsed = 0;
    const poll = setInterval(() => {
      elapsed += pollInterval;
      if (document.querySelector(cardSelector) || elapsed >= maxWait) {
        clearInterval(poll);
        launchTour();
      }
    }, pollInterval);
  }, [userId, markStarted, storageKey]);
  useEffect(() => {
    return () => { if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; } };
  }, []);

  return { startTour, tourStarted };
}

export function getMessagesTourStorageKey(userId) {
  return userId ? `messages_tour_started_${userId}` : 'messages_tour_started';
}

export function buildMessagesTourSteps() {
  const rawSteps = [
    {
      element: '[data-tour="messages-tab-requests"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Requests', _stepBody: 'View and manage all your connection requests both received and sent in one place.' },
    },
    {
      element: '[data-tour="messages-subtab-received"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Received Requests', _stepBody: 'Check the user\'s initial message and profile details. You can decide to Accept to start a conversation or Decline to reject the request.' },
    },
    {
      element: '[data-tour="messages-subtab-sent"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Send Requests', _stepBody: 'View the connection requests you\'ve sent and track their status here.' },
    },
    {
      element: '[data-tour="messages-tab-connections"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'My Connections', _stepBody: 'Manage conversations and collaborations with your connected partners.' },
    },
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function useMessagesTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getMessagesTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch { return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getMessagesTourStorageKey(userId)));
  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch { /* ignore */ }
    setTourStarted(true);
  }, []);

  const startTour = useCallback(() => {
    if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; }

    // All 4 steps — always defined, tab switching handled in onNextClick
    const allSteps = [
      {
        element: '[data-tour="messages-tab-requests"]',
        _beforeShow: null,
        popover: { side: 'bottom', align: 'start', _stepTitle: 'Requests', _stepBody: 'View and manage all your connection requests both received and sent in one place.' },
      },
      {
        element: '[data-tour="messages-subtab-received"]',
        _beforeShow: '[data-tour="messages-tab-requests"]', // click Requests tab first
        popover: { side: 'bottom', align: 'start', _stepTitle: 'Received Requests', _stepBody: "Check the user's initial message and profile details. You can Accept to start a conversation or Decline to reject the request." },
      },
      {
        element: '[data-tour="messages-subtab-sent"]',
        _beforeShow: '[data-tour="messages-subtab-sent"]', // click Sent sub-tab first
        popover: { side: 'bottom', align: 'start', _stepTitle: 'Sent Requests', _stepBody: "View the connection requests you've sent and track their status here." },
      },
      {
        element: '[data-tour="messages-tab-connections"]',
        _beforeShow: '[data-tour="messages-tab-connections"]', // click My Connections tab to switch back
        popover: { side: 'bottom', align: 'start', _stepTitle: 'My Connections', _stepBody: 'Manage conversations and collaborations with your connected partners.' },
      },
    ];

    const total = allSteps.length;
    allSteps.forEach((step, i) => {
      step.popover.title = ' ';
      step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
      delete step.popover._stepTitle;
      delete step.popover._stepBody;
    });

    // Move to a step: optionally click a tab first, wait for DOM, then drive
    const goToStep = (instance, steps, index) => {
      const step = steps[index];
      if (!step) return;

      const tabToClick = step._beforeShow;
      const doMove = () => {
        const el = step.element ? document.querySelector(step.element) : null;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => instance.moveTo(index), 150);
      };

      if (tabToClick) {
        const tabEl = document.querySelector(tabToClick);
        if (tabEl) tabEl.click();
        setTimeout(doMove, 500); // wait for React re-render
      } else {
        doMove();
      }
    };

    const driverInstance = driver({
      animate: true, smoothScroll: false,
      overlayColor: 'rgba(0, 0, 0, 0.55)', overlayOpacity: 0.55,
      stagePadding: 8, stageRadius: 10,
      popoverClass: 'fd-tour-popover', showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next', doneBtnText: 'Done', closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'instant', block: 'center' },
      onHighlightStarted: (element) => {
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;
        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());
          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);
          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }
        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();
          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },
      onNextClick: () => {
        const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
        const nextIndex = currentIndex + 1;
        if (nextIndex >= allSteps.length) {
          driverInstance.destroy();
          return;
        }
        goToStep(driverInstance, allSteps, nextIndex);
      },
      onDestroyStarted: () => { driverInstance.destroy(); },
      steps: allSteps,
    });

    driverRef.current = driverInstance;
    // Start at step 0 — Requests tab is always in DOM
    driverInstance.drive();
    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => { if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; } };
  }, []);

  return { startTour, tourStarted };
}

export function getUserManagementTourStorageKey(userId) {
  return userId ? `user_management_tour_started_${userId}` : 'user_management_tour_started';
}

// Session key used to resume the tour after a page navigation
const UM_TOUR_RESUME_KEY = 'um_tour_resume_step';

// All 9 steps with their target page so we know when to navigate
export const UM_ALL_STEPS = [
  { element: '[data-tour="usermgmt-nav-toggle"]', page: 'listings', popover: { side: 'right', align: 'start', _stepTitle: 'User Management', _stepBody: 'Manage users, roles, and team structure from one place.' } },
  { element: '[data-tour="usermgmt-nav-listings"]', page: 'listings', popover: { side: 'right', align: 'start', _stepTitle: 'User Listings', _stepBody: 'View and manage all users and their access in one place.' } },
  { element: '[data-tour="usermgmt-empty-state-image"]', page: 'listings', popover: { side: 'bottom', align: 'start', _stepTitle: 'No Users Found', _stepBody: 'This shows that no users have been added to your organization yet.' } },
  { element: '[data-tour="usermgmt-create-user-btn"]', page: 'listings', popover: { side: 'bottom', align: 'start', _stepTitle: 'Create New User', _stepBody: 'Add a new user and assign their role and access.' } },
  { element: '[data-tour="usermgmt-filter-btn"]', page: 'listings', popover: { side: 'bottom', align: 'start', _stepTitle: 'Filter By', _stepBody: 'Apply filters to narrow down users based on your criteria.' } },
  { element: '[data-tour="usermgmt-roles-empty-state-image"]', page: 'roles', popover: { side: 'bottom', align: 'start', _stepTitle: 'No Custom Roles', _stepBody: 'This indicates that no custom roles have been created yet.' } },
  { element: '[data-tour="usermgmt-create-role-btn"]', page: 'roles', popover: { side: 'bottom', align: 'start', _stepTitle: 'Create Role', _stepBody: 'Define a role to control what users can access and do.' } },
  { element: '[data-tour="usermgmt-manage-permissions-btn"]', page: 'roles', popover: { side: 'bottom', align: 'start', _stepTitle: 'Manage Permissions', _stepBody: 'Control what users can view, edit, and manage across the system.' } },
  { element: '[data-tour="usermgmt-user-name"]', page: 'listings', popover: { side: 'bottom', align: 'start', _stepTitle: 'User Name', _stepBody: 'Click on a user to view or edit details.' } },
];

const TOTAL_UM_STEPS = UM_ALL_STEPS.length;

function buildUmPopoverDescription(stepIndex) {
  const step = UM_ALL_STEPS[stepIndex];
  return stepContent(step.popover._stepTitle, step.popover._stepBody, stepIndex + 1, TOTAL_UM_STEPS);
}

function launchUmTourFromStep(startIndex, navigateFn, driverRef, markStarted, storageKey) {
  if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; }

  // Collect steps for the current page segment starting at startIndex
  const currentPage = UM_ALL_STEPS[startIndex]?.page;
  const segmentSteps = [];
  for (let i = startIndex; i < UM_ALL_STEPS.length; i++) {
    if (UM_ALL_STEPS[i].page !== currentPage) break;
    segmentSteps.push({ ...UM_ALL_STEPS[i], _globalIndex: i });
  }

  // Filter to only steps whose element exists in DOM
  const visibleSegment = segmentSteps.filter(s => !s.element || !!document.querySelector(s.element));
  if (visibleSegment.length === 0) {
    // Nothing visible on this page — skip to next page
    const nextPageStep = UM_ALL_STEPS.findIndex((s, i) => i > startIndex && s.page !== currentPage);
    if (nextPageStep !== -1) {
      const nextPage = UM_ALL_STEPS[nextPageStep].page;
      sessionStorage.setItem(UM_TOUR_RESUME_KEY, String(nextPageStep));
      navigateFn(nextPage === 'roles' ? '/roles-and-permissions' : '/user-role-management');
    }
    return;
  }

  // Build driver.js steps for this segment
  const driverSteps = visibleSegment.map(s => ({
    element: s.element,
    popover: {
      title: ' ',
      description: buildUmPopoverDescription(s._globalIndex),
      side: s.popover.side,
      align: s.popover.align,
    },
  }));

  const driverInstance = driver({
    animate: true, smoothScroll: true,
    overlayColor: 'rgba(0, 0, 0, 0.55)', overlayOpacity: 0.55,
    stagePadding: 8, stageRadius: 10,
    popoverClass: 'fd-tour-popover', showProgress: false,
    allowHTMLInPopover: true,
    nextBtnText: 'Next', doneBtnText: 'Done', closeBtnText: 'Skip',
    scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },
    onHighlightStarted: (element) => {
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    onPopoverRender: (popover) => {
      const titleEl = popover.title;
      const footerEl = popover.footer;
      if (titleEl) {
        const localIndex = driverInstance.getActiveIndex?.() ?? 0;
        const globalIndex = visibleSegment[localIndex]?._globalIndex ?? localIndex;
        titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());
        const labelSpan = document.createElement('span');
        labelSpan.className = 'fd-title-text';
        labelSpan.textContent = 'Tutorials';
        titleEl.insertBefore(labelSpan, titleEl.firstChild);
        const counter = document.createElement('span');
        counter.className = 'fd-progress-counter';
        counter.textContent = `${globalIndex + 1} of ${TOTAL_UM_STEPS}`;
        titleEl.appendChild(counter);
      }
      if (footerEl) {
        const oldSkip = footerEl.querySelector('.fd-skip-btn');
        if (oldSkip) oldSkip.remove();
        const skipBtn = document.createElement('button');
        skipBtn.className = 'fd-skip-btn';
        skipBtn.textContent = 'Skip';
        skipBtn.type = 'button';
        skipBtn.addEventListener('click', () => {
          sessionStorage.removeItem(UM_TOUR_RESUME_KEY);
          driverInstance.destroy();
        });
        footerEl.insertBefore(skipBtn, footerEl.firstChild);
      }
    },
    onNextClick: () => {
      const localIndex = driverInstance.getActiveIndex?.() ?? 0;
      const isLastInSegment = localIndex >= visibleSegment.length - 1;

      if (isLastInSegment) {
        // Find the next global step on a different page
        const lastGlobal = visibleSegment[visibleSegment.length - 1]._globalIndex;
        let nextGlobal = lastGlobal + 1;
        while (nextGlobal < UM_ALL_STEPS.length && UM_ALL_STEPS[nextGlobal].page === currentPage) {
          nextGlobal++;
        }

        if (nextGlobal >= UM_ALL_STEPS.length) {
          // Tour complete
          sessionStorage.removeItem(UM_TOUR_RESUME_KEY);
          driverInstance.destroy();
          return;
        }

        const nextPage = UM_ALL_STEPS[nextGlobal].page;
        sessionStorage.setItem(UM_TOUR_RESUME_KEY, String(nextGlobal));
        driverInstance.destroy();
        navigateFn(nextPage === 'roles' ? '/roles-and-permissions' : '/user-role-management');
        return;
      }

      // Normal next within segment
      const nextEl = visibleSegment[localIndex + 1]?.element
        ? document.querySelector(visibleSegment[localIndex + 1].element)
        : null;
      if (nextEl) {
        nextEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => driverInstance.moveNext(), 300);
      } else {
        driverInstance.moveNext();
      }
    },
    onDestroyStarted: () => driverInstance.destroy(),
    onDestroyed: () => { driverRef.current = null; },
    steps: driverSteps,
  });

  driverRef.current = driverInstance;

  // Scroll first element into view before starting
  const firstEl = visibleSegment[0]?.element ? document.querySelector(visibleSegment[0].element) : null;
  if (firstEl) {
    firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => driverInstance.drive(), 400);
  } else {
    driverInstance.drive();
  }

  markStarted(storageKey);
}

export function useUserManagementTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getUserManagementTourStorageKey(userId);
  const navigate = useNavigate();
  const location = useLocation();

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch { return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getUserManagementTourStorageKey(userId)));
  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  // Support resetting the local storage tour flag via URL query parameter for testing/development
  useEffect(() => {
    const isForceReset =
      window.location.search.includes('reset_tour=true') ||
      window.location.search.includes('reset_tours=true');
    if (isForceReset) {
      try {
        localStorage.removeItem(storageKey);
        sessionStorage.removeItem(UM_TOUR_RESUME_KEY);
        setTourStarted(false);
      } catch {
        // ignore
      }
    }
  }, [storageKey]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch { /* ignore */ }
    setTourStarted(true);
  }, []);

  // Auto-start User Management / Roles & Permissions tour on first arrival if never started
  useEffect(() => {
    if (!userId || tourStarted) return;
    const pathname = location.pathname;
    const isListingsPage = pathname.startsWith('/user-role-management');
    const isRolesPage = pathname.startsWith('/roles-and-permissions');
    if (isListingsPage || isRolesPage) {
      sessionStorage.setItem(UM_TOUR_RESUME_KEY, isListingsPage ? '0' : '5');
    }
  }, [location.pathname, tourStarted, userId]);

  // Resume tour whenever the pathname changes (DashboardLayout never unmounts)
  useEffect(() => {
    const resumeStep = sessionStorage.getItem(UM_TOUR_RESUME_KEY);
    if (resumeStep === null) return;

    const stepIndex = parseInt(resumeStep, 10);
    if (isNaN(stepIndex) || stepIndex >= UM_ALL_STEPS.length) {
      sessionStorage.removeItem(UM_TOUR_RESUME_KEY);
      return;
    }

    const currentPage = UM_ALL_STEPS[stepIndex]?.page;
    const pathname = location.pathname;
    const onListings = pathname.startsWith('/user-role-management');
    const onRoles = pathname.startsWith('/roles-and-permissions');

    const isCorrectPage =
      (currentPage === 'listings' && onListings) ||
      (currentPage === 'roles' && onRoles);

    if (!isCorrectPage) return;

    // Poll for the main page container to appear and ensure any loading overlays/spinners are gone
    const containerSelector = onListings ? '.user-listings' : '.roles-permissions';
    const maxWait = 10000;
    const interval = 100;
    let elapsed = 0;
    const poll = setInterval(() => {
      elapsed += interval;
      const containerEl = document.querySelector(containerSelector);
      const isStillLoading = document.querySelector('.table-loading') || document.querySelector('.page-loader');
      if ((containerEl && !isStillLoading) || elapsed >= maxWait) {
        clearInterval(poll);
        launchUmTourFromStep(stepIndex, navigate, driverRef, markStarted, storageKey);
      }
    }, interval);

    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const startTour = useCallback(() => {
    sessionStorage.removeItem(UM_TOUR_RESUME_KEY);
    const pathname = window.location.pathname;
    if (!pathname.startsWith('/user-role-management')) {
      sessionStorage.setItem(UM_TOUR_RESUME_KEY, '0');
      navigate('/user-role-management');
      return;
    }
    launchUmTourFromStep(0, navigate, driverRef, markStarted, storageKey);
  }, [navigate, markStarted, storageKey]);

  useEffect(() => {
    return () => {
      if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; }
    };
  }, []);

  return { startTour, tourStarted };
}

export function getSkuSalesTourStorageKey(userId) {
  return userId ? `sku_sales_tour_started_${userId}` : 'sku_sales_tour_started';
}

export function buildSkuSalesTourSteps() {
  const rawSteps = [
    {
      element: '[data-tour="sku-sales-download-csv"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Download CSV Template', _stepBody: 'Download a pre-formatted template to upload your data correctly.' },
    },
    {
      element: '[data-tour="sku-sales-talk-to-sales"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Talk to Sales', _stepBody: 'Connect with our team to set up integrations and get started.' },
    },
    {
      element: '[data-tour="sku-sales-bulk-sync"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Bulk Sync', _stepBody: 'Upload and sync multiple SKUs quickly and efficiently.' },
    },
    {
      element: '[data-tour="sku-sales-case-config"]',
      popover: { side: 'top', align: 'start', _stepTitle: 'Case Configuration', _stepBody: 'Define how your product cases are structured for accurate quantity.' },
    },
    {
      element: '[data-tour="sku-sales-new-sku"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'New SKUs', _stepBody: 'Add a new SKU to your catalog for tracking and sales.' },
    },
  ];

  const visibleSteps = rawSteps.filter(s => !s.element || !!document.querySelector(s.element));
  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function useSkuSalesTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getSkuSalesTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch { return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getSkuSalesTourStorageKey(userId)));
  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch { /* ignore */ }
    setTourStarted(true);
  }, []);

  const startTour = useCallback((onComplete) => {
    if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; }

    const visibleSteps = buildSkuSalesTourSteps();
    if (visibleSteps.length === 0) { console.warn('[useSkuSalesTour] No visible tour elements found.'); return; }

    const driverInstance = driver({
      animate: true, smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)', overlayOpacity: 0.55,
      stagePadding: 8, stageRadius: 10,
      popoverClass: 'fd-tour-popover', showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next', doneBtnText: 'Done', closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },
      onHighlightStarted: (element) => {
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;
        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());
          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);
          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }
        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();
          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },
      onNextClick: () => {
        const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
        const nextStep = visibleSteps[currentIndex + 1];
        const nextEl = nextStep?.element ? document.querySelector(nextStep.element) : null;
        if (nextEl) {
          nextEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => { driverInstance.moveNext(); }, 400);
        } else {
          driverInstance.moveNext();
        }
      },
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => {
        driverRef.current = null;
        if (typeof onComplete === 'function') onComplete();
      },
      steps: visibleSteps,
    });

    driverRef.current = driverInstance;

    const firstEl = visibleSteps[0]?.element ? document.querySelector(visibleSteps[0].element) : null;
    if (firstEl) {
      firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { driverInstance.drive(); }, 500);
    } else {
      driverInstance.drive();
    }

    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => { if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; } };
  }, []);

  return { startTour, tourStarted };
}

export function getTeamManagementTourStorageKey(userId) {
  return userId ? `team_management_tour_started_${userId}` : 'team_management_tour_started';
}

export function buildTeamManagementTourSteps() {
  const isMobile = window.innerWidth <= 768;

  // On mobile the assign button is hidden inside the expanded card.
  // Use the mobile-specific selector; it will only exist after the card is expanded.
  const assignSelector = isMobile
    ? '[data-tour="team-mgmt-assign-team-mobile"]'
    : '[data-tour="team-mgmt-assign-team"]';

  const rawSteps = [
    {
      element: '[data-tour="team-mgmt-create-team"]',
      popover: { side: 'bottom', align: 'start', _stepTitle: 'Team management', _stepBody: 'Create a team to group users and manage responsibilities.' },
    },
    {
      element: assignSelector,
      _isMobileAssign: isMobile,
      popover: { side: 'top', align: 'start', _stepTitle: 'Assign Team', _stepBody: 'Assign users to a team to define responsibilities and ownership.' },
    },
  ];

  // For mobile the assign element won't exist yet (card collapsed), so include it unconditionally
  // and handle expansion in onNextClick.
  const visibleSteps = rawSteps.filter(s => {
    if (!s.element) return true;
    if (s._isMobileAssign) return true; // always include; we'll expand card first
    return !!document.querySelector(s.element);
  });

  const total = visibleSteps.length;
  visibleSteps.forEach((step, i) => {
    step.popover.title = ' ';
    step.popover.description = stepContent(step.popover._stepTitle, step.popover._stepBody, i + 1, total);
    delete step.popover._stepTitle;
    delete step.popover._stepBody;
  });
  return visibleSteps;
}

export function useTeamManagementTour(userId) {
  const driverRef = useRef(null);
  const storageKey = getTeamManagementTourStorageKey(userId);

  const readFlag = useCallback((key) => {
    try { return localStorage.getItem(key) === 'true'; } catch { return false; }
  }, []);

  const [tourStarted, setTourStarted] = useState(() => readFlag(getTeamManagementTourStorageKey(userId)));
  useEffect(() => { setTourStarted(readFlag(storageKey)); }, [storageKey, readFlag]);

  const markStarted = useCallback((key) => {
    try { localStorage.setItem(key, 'true'); } catch { /* ignore */ }
    setTourStarted(true);
  }, []);

  const startTour = useCallback((onComplete) => {
    if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; }

    const isMobile = window.innerWidth <= 768;
    const visibleSteps = buildTeamManagementTourSteps();
    if (visibleSteps.length === 0) { console.warn('[useTeamManagementTour] No visible tour elements found.'); return; }

    const driverInstance = driver({
      animate: true, smoothScroll: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)', overlayOpacity: 0.55,
      stagePadding: 8, stageRadius: 10,
      popoverClass: 'fd-tour-popover', showProgress: false,
      allowHTMLInPopover: true,
      nextBtnText: 'Next', doneBtnText: 'Done', closeBtnText: 'Skip',
      scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },
      onHighlightStarted: (element) => {
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      onPopoverRender: (popover) => {
        const titleEl = popover.title;
        const footerEl = popover.footer;
        if (titleEl) {
          const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
          const total = visibleSteps.length;
          titleEl.querySelectorAll('.fd-title-text, .fd-progress-counter').forEach(n => n.remove());
          const labelSpan = document.createElement('span');
          labelSpan.className = 'fd-title-text';
          labelSpan.textContent = 'Tutorials';
          titleEl.insertBefore(labelSpan, titleEl.firstChild);
          const counter = document.createElement('span');
          counter.className = 'fd-progress-counter';
          counter.textContent = `${currentIndex + 1} of ${total}`;
          titleEl.appendChild(counter);
        }
        if (footerEl) {
          const oldSkip = footerEl.querySelector('.fd-skip-btn');
          if (oldSkip) oldSkip.remove();
          const skipBtn = document.createElement('button');
          skipBtn.className = 'fd-skip-btn';
          skipBtn.textContent = 'Skip';
          skipBtn.type = 'button';
          skipBtn.addEventListener('click', () => driverInstance.destroy());
          footerEl.insertBefore(skipBtn, footerEl.firstChild);
        }
      },
      onNextClick: () => {
        const currentIndex = driverInstance.getActiveIndex?.() ?? 0;
        const nextStep = visibleSteps[currentIndex + 1];
        if (!nextStep) { driverInstance.destroy(); return; }

        // On mobile, before showing the assign step we need to expand the first card
        if (isMobile && nextStep._isMobileAssign) {
          const cardRow = document.querySelector('[data-tour="team-mgmt-mobile-card-row"]');
          if (cardRow) {
            cardRow.click(); // expand the card
            setTimeout(() => {
              const assignEl = document.querySelector('[data-tour="team-mgmt-assign-team-mobile"]');
              if (assignEl) {
                assignEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Update the step element now that it exists in DOM
                nextStep.element = '[data-tour="team-mgmt-assign-team-mobile"]';
              }
              setTimeout(() => { driverInstance.moveNext(); }, 400);
            }, 350);
            return;
          }
        }

        const nextEl = nextStep.element ? document.querySelector(nextStep.element) : null;
        if (nextEl) {
          nextEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => { driverInstance.moveNext(); }, 400);
        } else {
          driverInstance.moveNext();
        }
      },
      onDestroyStarted: () => driverInstance.destroy(),
      onDestroyed: () => {
        driverRef.current = null;
        if (typeof onComplete === 'function') onComplete();
      },
      steps: visibleSteps,
    });

    driverRef.current = driverInstance;

    const firstTmEl = visibleSteps[0]?.element ? document.querySelector(visibleSteps[0].element) : null;
    if (firstTmEl) {
      firstTmEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => { driverInstance.drive(); }, 500);
    } else {
      driverInstance.drive();
    }

    markStarted(storageKey);
  }, [userId, markStarted, storageKey]);

  useEffect(() => {
    return () => { if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* ignore */ } driverRef.current = null; } };
  }, []);

  return { startTour, tourStarted };
}